<script setup lang="ts">
import {computed, ref} from "vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import {Job} from "@/store/jobs";
import {copyTextToClipboard} from "@/utils/clipboard";
import {buildAbsoluteClientUrl} from "@/utils/urlHelpers";

const props = defineProps<{
	job: Job;
	showSendToChecking?: boolean;
	showEndByWorker?: boolean;
	showEndByWorkerWithMessage?: boolean;
	showEndByCreator?: boolean;
	showSendToReworking?: boolean;
	showEndByAdmin?: boolean;
	showEdit?: boolean;
	showDelete?: boolean;
}>()

const emit = defineEmits<{
	(e: 'on-show-info'): void,
	(e: 'on-edit'): void,
	(e: 'on-delete'): void,
	(e: 'on-create-linked'): void,
	(e: 'on-show-linked'): void,
	(e: 'on-comment'): void,
	(e: 'on-send-to-checking'): void,
	(e: 'on-end-by-worker'): void,
	(e: 'on-end-by-worker-with-message'): void,
	(e: 'on-end-by-creator'): void,
	(e: 'on-send-to-reworking'): void,
	(e: 'on-end-by-admin'): void,
}>()

// Верхняя секция (связанные работы, комментировать)
const hasTopActions = computed(() => false)

// Видимость разделителя между статус-действиями и остальным меню
const hasStatusActions = computed(() =>
	props.showSendToReworking
	|| props.showEndByWorker
	|| props.showEndByCreator
	|| props.showEndByWorkerWithMessage
	|| props.showEndByAdmin
	|| props.showSendToChecking
)

const $tooltip = ref<typeof AppTooltip | null>(null)

const onCopyLink = async () => {
	let target: { page: string; id: number } | null = null
	if (props.job.Project) {
		target = { page: 'project', id: props.job.Project.Id }
	} else if (props.job.Clients?.[0]) {
		target = { page: 'client', id: props.job.Clients[0].Id }
	}

	if (!target) {
		return
	}

	const copied = await copyTextToClipboard(
		buildAbsoluteClientUrl(`main?page=${target.page}&id=${target.id}&jobId=${props.job.Id}`)
	)
	if (!copied) {
		return
	}

	$tooltip.value?.showByClick()
}
</script>

<template>
	<div class="job-options-dropdown">
		<!-- 1. Создать связанную работу -->
		<div
			v-if="false"
			class="job-options-dropdown__item"
			@click="emit('on-create-linked')"
		>
			<app-icon name="create-related-work" width="18px" color="#979797" />
			<div class="job-options-dropdown__item-text">{{ $t('job_create_related') }}</div>
		</div>
		<!-- 2. Показать связанные работы -->
		<div
			v-if="false"
			class="job-options-dropdown__item"
			@click="emit('on-show-linked')"
		>
			<app-icon name="related" width="18px" color="#979797" />
			<div class="job-options-dropdown__item-text">{{ $t('job_show_related') }}</div>
		</div>
		<!-- 3. Комментировать -->
		<div
			v-if="false"
			class="job-options-dropdown__item"
			@click="emit('on-comment')"
		>
			<app-icon name="comment-arrow" width="18px" color="#979797" />
			<div class="job-options-dropdown__item-text">{{ $t('job_comment_action') }}</div>
		</div>

		<div v-if="hasTopActions && hasStatusActions" class="job-options-dropdown__divider" />

		<!-- 5. Вернуть на доработку -->
		<div
			v-if="showSendToReworking"
			class="job-options-dropdown__item job-options-dropdown__item--warning"
			@click="emit('on-send-to-reworking')"
		>
			<app-icon name="back-to-rework" width="18px" color="#F9C22F" />
			<div class="job-options-dropdown__item-text">{{ $t('job_action_return_to_rework') }}</div>
		</div>
		<!-- 6. Завершить работу (endByWorker без сообщения / endByCreator) -->
		<div
			v-if="showEndByWorker"
			class="job-options-dropdown__item"
			@click="emit('on-end-by-worker')"
		>
			<app-icon name="job-finish" width="18px" color="#3F884F" />
			<div class="job-options-dropdown__item-text">{{ $t('job_action_finish') }}</div>
		</div>
		<div
			v-if="showEndByCreator"
			class="job-options-dropdown__item"
			@click="emit('on-end-by-creator')"
		>
			<app-icon name="job-finish" width="18px" color="#3F884F" />
			<div class="job-options-dropdown__item-text">{{ $t('job_action_accept') }}</div>
		</div>
		<!-- 7. Завершить с отчётом (endByWorker с сообщением) -->
		<div
			v-if="showEndByWorkerWithMessage"
			class="job-options-dropdown__item"
			@click="emit('on-end-by-worker-with-message')"
		>
			<app-icon name="job-finish" width="18px" color="#3F884F" />
			<div class="job-options-dropdown__item-text">{{ $t('job_action_finish_with_report') }}</div>
		</div>
		<!-- 8. Завершить как администратор (поведение зависит от NeedChecking в родителе) -->
		<div
			v-if="showEndByAdmin"
			class="job-options-dropdown__item"
			@click="emit('on-end-by-admin')"
		>
			<app-icon
				name="end-by-admin"
				width="18px"
				color="#607D8B"
			/>
			<div class="job-options-dropdown__item-text">{{ $t('job_action_finish_as_admin') }}</div>
		</div>
		<!-- 9. Отправить на проверку -->
		<div
			v-if="showSendToChecking"
			class="job-options-dropdown__item job-options-dropdown__item--warning"
			@click="emit('on-send-to-checking')"
		>
			<app-icon name="send-to-check" width="18px" color="#F9C22F" />
			<div class="job-options-dropdown__item-text">{{ $t('job_action_send_to_checking') }}</div>
		</div>

		<div v-if="hasStatusActions" class="job-options-dropdown__divider" />

		<!-- 11. Поделиться ссылкой -->
		<app-tooltip
			ref="$tooltip"
			class="job-options-dropdown__share-tooltip"
			:text="$t('link_copied_to_clipboard')"
			position="right"
			show-by-activator-click
		>
			<template #activator>
				<div
					class="job-options-dropdown__item"
					@click="onCopyLink"
				>
					<app-icon
						name="share"
						width="18px"
						color="#979797"
					/>
					<div class="job-options-dropdown__item-text">
						{{ $t('ui_share_link') }}
					</div>
				</div>
			</template>
		</app-tooltip>
		<!-- 12. Посмотреть информацию -->
		<div
			class="job-options-dropdown__item"
			@click="emit('on-show-info')"
		>
			<app-icon
				name="info-circle-outline"
				width="18px"
				color="#979797"
			/>
			<div class="job-options-dropdown__item-text">
				{{ $t('ui_view_info') }}
			</div>
		</div>
		<!-- 13. Редактировать работу -->
		<div
			v-if="showEdit"
			class="job-options-dropdown__item"
			@click="emit('on-edit')"
		>
			<app-icon
				name="edit-square-outline"
				width="18px"
				color="#979797"
			/>
			<div class="job-options-dropdown__item-text">
				{{ $t('job_edit') }}
			</div>
		</div>
		<!-- 14. Удалить работу -->
		<div
			v-if="showDelete"
			class="job-options-dropdown__item job-options-dropdown__item--danger"
			@click="emit('on-delete')"
		>
			<app-icon
				name="delete-outline"
				width="18px"
				color="#979797"
			/>
			<div class="job-options-dropdown__item-text">
				{{ $t('job_delete') }}
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
.job-options-dropdown {
	display: flex;
	flex-direction: column;
	gap: 2px;
	background: #fff;
	padding: 2px;

	&__item {
		display: flex;
		align-items: center;
		gap: 8px;
		width: 100%;
		padding: 3px 8px;
		border-radius: 8px;
		box-sizing: border-box;

		&:hover {
			background-color: #F2F2F2;
			cursor: pointer;

			:deep(.svg-icon) {
				color: #3F884F !important;
			}
		}

		&--danger:hover {
			:deep(.svg-icon) {
				color: #BA4C4C !important;
			}
		}

		&-text {
			font-size: 14px;
			line-height: 18px;
			color: #4D4D4D;
		}
	}

	&__divider {
		height: 1px;
		background: #E5E5E5;
		margin: 3px 0;
	}

	&::v-deep(.app-tooltip__activator) {
		width: 100%;
	}

	&__share-tooltip {
		height: 24px;
	}
}

@media (max-width: 480px) {
	.job-options-dropdown {
		width: 100%;
		height: auto;
		gap: 0;
		padding: 8px 16px 24px;
		box-sizing: border-box;
		overflow-y: auto;

		&__item {
			min-height: 44px;
			padding: 0 8px;
			border-bottom: 1px solid #F2F2F2;
			border-radius: 0;
		}

		> .job-options-dropdown__item:last-child {
			border-bottom: none;
		}

		&__divider {
			display: none;
		}

		&__share-tooltip {
			height: auto;
		}
	}
}
</style>

